import React, { useEffect, useRef, useState } from 'react';

import { Slider } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';

import styles from 'pages/Dashboard/AcceptedOfferDetails/UpdateLoanTerms/UpdateLoanTerms.module.scss';
import FormBody from 'components/FormBody/index';
import { useAppSelector } from 'hooks/useAppSelector';
import { Lender, LoanTerm } from 'enums/ApplicationsVariables';
import { QUILO_FIXED_RATE_DESCRIPTION } from 'pages/Dashboard/steps/AcceptOffer/acceptOffer.constants';
import LoanOfferCard from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/LoanOfferCard/LoanOfferCard';
import LoanOfferOptions from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptOneTermOffer/LoanOfferOptions/LoanOfferOptions';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApplicationProductName } from 'enums/common.enums';
import { OfferResponseDto } from 'api/AutomatedOffersApi';
import NumberInput from 'components/Inputs/NumberInput';
import { maskMonetaryValue, MONETARY_PREFIX, removeCurrencySign, removeNonDigits } from 'utils/stringUtils';
import { MAX_LOAN_AMOUNT_LENGTH } from 'utils/constants';
import Select from 'components/Select';
import { Button } from 'components/Buttons/Button';
import { OptionType } from 'utils/inputSelect';
import { Text } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/acceptAutomatedOffer.enums';
import { ButtonType } from 'enums/ButtonType';
import BlurPopup from 'components/BlurPopup';
import { LoadingType, setLoadingState } from 'actions';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import Loader from 'components/Loader';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';

import { LOAN_AMOUNT_SLIDER_STEP } from './updateLoanTerms.constants';
import {
  getAllowedStatuses,
  getMaxLoanAmount,
  getMinLoanAmount,
  getLoanTerms,
  getOfferApi,
} from '../acceptOfferDetails.utils';

interface FormValues {
  loanAmount: number;
  loanTerm: string;
}

const UpdateLoanTerms = () => {
  const application = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false);
  const [isDirtyForm, setIsDirtyForm] = useState(false);
  const dispatch = useAppDispatch();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isAnyLenderQuilo, setIsAnyLenderQuilo] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, productName, variables } = useAppSelector((state) => state.application);
  const [offer, setOffer] = useState<OfferResponseDto>();
  const maxLoanAmount = offer?.maxLoanAmount || getMaxLoanAmount(productName!);
  const minLoanAmount = offer?.minLoanAmount || getMinLoanAmount(productName!);
  const {
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    control,
    handleSubmit,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      loanAmount: offer?.loanAmount || variables!.loan_amount!,
      loanTerm: offer?.termYears || variables!.loan_term!,
    },
    shouldFocusError: true,
  });
  const watcher = watch();

  const offerApi = useRef(() =>
    getOfferApi({
      productName: productName as ApplicationProductName,
      hasAutomatedOffer: variables!.hasAutomatedOffer as boolean,
    }),
  );

  const getOffer = async (loanAmount: number, loanTerm: string) => {
    const getLoans = async () => {
      try {
        setIsLoading(true);

        const api = offerApi.current();
        const response = await api.getOffer(applicationId!, loanAmount, loanTerm);

        setOffer(response);

        setIsAnyLenderQuilo(response.lender.includes(Lender.Quilo));
      } catch (error) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        setIsLoading(false);
      }
    };

    getLoans();
  };

  const updateOffer = async (loanAmount: number, loanTerm: string) => {
    const api = offerApi.current();

    try {
      setIsLoading(true);

      await api.updateOffer({ applicationId: applicationId as string, loanAmount: loanAmount, loanTerm: loanTerm });
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOffer(variables!.loan_amount!, variables!.loan_term!);
  }, [applicationId]);

  const handleLoanAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +removeNonDigits(removeCurrencySign(event.target.value));

    if (watcher.loanAmount !== newValue) {
      clearErrors('loanAmount');
      setValue('loanAmount', newValue);
      getOffer(newValue, watcher.loanTerm);
    }

    if (isSubmitted) {
      trigger();
    }
  };

  const getSliderMarks = (minValue: number, maxValue: number) => {
    return [
      {
        value: minValue,
        label: `${maskMonetaryValue(minValue)}`,
      },
      {
        value: maxValue,
        label: `${maskMonetaryValue(maxValue)}`,
      },
    ];
  };

  const onSubmit = handleSubmit(() => {
    setIsConfirmationPopupOpen(true);
  });

  const handleLoanAmountSliderChangeCommitted = (
    event: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[],
  ) => {
    clearErrors('loanAmount');
    setValue('loanAmount', value as number);
    getOffer(value as number, watcher.loanTerm);

    if (isSubmitted) {
      trigger();
    }
  };

  const handleClickClose = () => {
    setIsConfirmationPopupOpen(false);
  };

  const handleConfirmButton = async () => {
    try {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));
      await updateOffer(watcher.loanAmount, watcher.loanTerm);
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsConfirmationPopupOpen(false);
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      setIsDirtyForm(false);
    }
  };

  useEffect(() => {
    setIsDirtyForm(
      watcher.loanAmount !== application.variables!.loan_amount ||
        watcher.loanTerm !== application.variables!.loan_term,
    );
  }, [watcher.loanAmount, watcher.loanTerm, application.variables]);

  const loanTermOptions = getLoanTerms(
    productName!,
    variables!.lender as Lender,
    variables!.maximum_transunion_vantage_score_v4 ?? 0,
    offer?.minLoanTerm as LoanTerm,
  );
  const isUpdateAllowed = getAllowedStatuses(productName!).includes(application.applicationStatus!);

  return isLoading || isGlobalLoading ? (
    <Loader customContainerClassname={styles.loaderContainer} />
  ) : (
    <div className={styles.acceptOfferContainer}>
      {offer && (
        <>
          <FormBody formBodyClassName={styles.formBody} onSubmit={onSubmit}>
            {isUpdateAllowed && (
              <div className={styles.bodyTermsChanges}>
                <Controller
                  name="loanAmount"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (value > maxLoanAmount) {
                        return `Cash to You must be less or equal to ${maskMonetaryValue(maxLoanAmount)}`;
                      }

                      if (value < minLoanAmount) {
                        return `Cash to You must be greater or equal to ${maskMonetaryValue(minLoanAmount)}`;
                      }
                    },
                  }}
                  render={({ field }) => (
                    <div className={styles.blockWrapper}>
                      <label className={styles.label}>{Text.CashToYou}</label>
                      <NumberInput
                        key={`input-${watcher.loanAmount}`}
                        ref={field.ref}
                        label=""
                        prefix={MONETARY_PREFIX}
                        placeholder={MONETARY_PREFIX}
                        thousandSeparator
                        inputContainerClassName={styles.formInput}
                        inputClassName={styles.loanAmountInput}
                        labelClassName={styles.loanAmountLabel}
                        name="loanAmount"
                        onBlur={handleLoanAmountInputChange}
                        errorMessage={errors.loanAmount?.message}
                        value={watcher.loanAmount}
                        maxLength={MAX_LOAN_AMOUNT_LENGTH}
                        allowNegative={false}
                        onKeyDown={(event: any) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();

                            handleLoanAmountInputChange(event);
                          }
                        }}
                      />
                    </div>
                  )}
                />
                <Slider
                  key={`slider-${watcher.loanAmount}`}
                  defaultValue={watcher.loanAmount}
                  step={LOAN_AMOUNT_SLIDER_STEP}
                  min={minLoanAmount}
                  max={maxLoanAmount}
                  valueLabelDisplay="auto"
                  classes={{
                    root: styles.loanAmountSlider,
                    mark: styles.loanAmountSliderMark,
                    markLabel: styles.loanAmountSliderMarkLabel,
                    rail: styles.loanAmountSliderRail,
                    thumb: styles.loanAmountSliderThumb,
                  }}
                  marks={getSliderMarks(minLoanAmount, maxLoanAmount)}
                  onChangeCommitted={handleLoanAmountSliderChangeCommitted}
                />
                {!!loanTermOptions.length && (
                  <div className={styles.blockWrapper}>
                    <label className={styles.label}>Term</label>
                    <Select
                      key={`select-${watcher.loanTerm}`}
                      name="loanTerm"
                      required
                      control={control}
                      options={loanTermOptions}
                      inputContainerClassName={styles.input}
                      className={styles.dropdown}
                      useFormControl
                      onChange={(event: any) => {
                        setValue('loanTerm', event.value);
                        trigger('loanTerm');
                        getOffer(watcher.loanAmount, event.value);
                      }}
                      value={loanTermOptions.find((option: OptionType) => watcher.loanTerm === option.value)}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={styles.offerCards}>
              <LoanOfferCard
                title=""
                loanOffer={{
                  loanAmount: offer.loanAmount,
                  term: +offer.termYears,
                  rate: offer.rate,
                  apr: offer.apr,
                  monthlyPayment: offer.monthlyPayment,
                  lender: offer.lender,
                  oneTimeFeePercentage: offer.oneTimeFeePercentage,
                  grossLoanAmount: offer.grossLoan,
                }}
                lender={''}
              />
              <LoanOfferOptions />
            </div>
            {isAnyLenderQuilo && <div className={styles.fixedRateDescription}>{QUILO_FIXED_RATE_DESCRIPTION}</div>}
            {isUpdateAllowed && (
              <div className={clsx('buttons', styles.submit_button)}>
                <Button type="submit" disabled={!isDirtyForm}>
                  Submit
                </Button>
              </div>
            )}
          </FormBody>
          <BlurPopup id="popup" onClose={handleClickClose} open={isConfirmationPopupOpen} scroll="body" blurDeep={8}>
            <span className={styles.popupText}>Confirmation</span>
            <div className={styles.popupBody}>Please Confirm The Changes In Your Loan</div>
            <div className={styles.dialogButtonsContainer}>
              <Button buttonType={ButtonType.Secondary} onClick={handleClickClose} withArrow>
                Back
              </Button>
              <Button buttonType={ButtonType.Primary} onClick={handleConfirmButton}>
                Confirm
              </Button>
            </div>
          </BlurPopup>
        </>
      )}
    </div>
  );
};

export default UpdateLoanTerms;
